/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate, Navigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import {
  CircularProgress,
  TextField,
  Button,
  Box,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Services
import dashboardService from "services/dashboardService";

// Context
import { UserContext } from "context/UserContext";

// Dashboard components
import OrdersOverview from "layouts/dashboard/components/OrdersOverview";
import TopCustomers from "layouts/dashboard/components/TopCustomers";
import SaltSalesBySector from "layouts/dashboard/components/SaltSalesBySector";
import CustomDoughnutChart from "layouts/dashboard/components/CustomDoughnutChart";
import CustomLineChart from "layouts/dashboard/components/CustomLineChart";
import CustomBarChart from "layouts/dashboard/components/CustomBarChart";
import FastestPayers from "layouts/dashboard/components/FastestPayers";
import BlackList from "layouts/dashboard/components/BlackList";
import DateTimeDisplay from "layouts/dashboard/components/DateTimeDisplay";

function Dashboard() {
  const { user, loading: userLoading } = useContext(UserContext);
  const navigate = useNavigate();
  const coreRoles = ["ROLE_MANAGER", "ROLE_SALES_RESPONSIBLE", "ROLE_CUSTOMER"];
  const userRoles = Array.isArray(user?.roles) ? user.roles : [];
  const hasCoreRole = userRoles.some((role) => coreRoles.includes(role));
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedStartDate, setSelectedStartDate] = useState("");
  const [selectedEndDate, setSelectedEndDate] = useState("");
  const [pendingDialogOpen, setPendingDialogOpen] = useState(false);

  useEffect(() => {
    function formatDateWithDashes(date) {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`; // Örn: 06-06-2025
    }

    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Tarih kontrolü - eğer tarih seçilmemişse son 30 gün
        let start, end;

        if (selectedStartDate && selectedEndDate) {
          // HTML5 date input formatından (YYYY-MM-DD) bizim formatımıza (DD-MM-YYYY) çevir
          const startParts = selectedStartDate.split("-");
          const endParts = selectedEndDate.split("-");
          start = `${startParts[2]}-${startParts[1]}-${startParts[0]}`;
          end = `${endParts[2]}-${endParts[1]}-${endParts[0]}`;
        } else {
          // Son 30 günlük veri için tarih aralığı
          const now = new Date();
          const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
          start = formatDateWithDashes(thirtyDaysAgo);
          end = formatDateWithDashes(now);
        }

        const data = await dashboardService.getDashboardData(start, end);
        setDashboardData(data);
      } catch (err) {
        console.error("Dashboard veri çekme hatası:", err);

        // Oturum hatası durumunda özel mesaj
        if (err.message?.includes("Oturum süreniz dolmuş")) {
          setError("Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.");
        } else {
          setError(err.message || "Veri yüklenirken hata oluştu");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [selectedStartDate, selectedEndDate]);

  const handleDateChange = () => {
    // Seçilen tarihleri aktif tarihlere ata
    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
  };

  const handleResetDates = () => {
    setStartDate("");
    setEndDate("");
    setSelectedStartDate("");
    setSelectedEndDate("");
  };

  const handlePendingInfoClick = () => {
    setPendingDialogOpen(true);
  };

  const handlePendingDialogClose = () => {
    setPendingDialogOpen(false);
  };

  const handleLastWeek = () => {
    const now = new Date();
    const lastWeek = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    setStartDate(formatDate(lastWeek));
    setEndDate(formatDate(now));
    setSelectedStartDate(formatDate(lastWeek));
    setSelectedEndDate(formatDate(now));
  };

  const handleLastDay = () => {
    const now = new Date();
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    setStartDate(formatDate(yesterday));
    setEndDate(formatDate(now));
    setSelectedStartDate(formatDate(yesterday));
    setSelectedEndDate(formatDate(now));
  };

  // Aktif tarih aralığını formatla
  const getActiveDateRange = () => {
    if (selectedStartDate && selectedEndDate) {
      const startParts = selectedStartDate.split("-");
      const endParts = selectedEndDate.split("-");
      // YYYY-MM-DD formatından dd-MM-YYYY formatına çevir
      const startFormatted = `${startParts[2]}-${startParts[1]}-${startParts[0]}`;
      const endFormatted = `${endParts[2]}-${endParts[1]}-${endParts[0]}`;
      return `${startFormatted} - ${endFormatted}`;
    }
    return "Son 30 gün";
  };

  // Salt türleri için doughnut chart verisi - optimize edilmiş
  const saltTypesChartData = useMemo(() => {
    return dashboardData?.saltTypes?.length > 0
      ? {
          labels: dashboardData.saltTypes.map((item) => item.saltType || "Bilinmeyen"),
          datasets: {
            label: "Tuz Türleri",
            data: dashboardData.saltTypes.map((item) => item.count || 0),
            backgroundColors: ["info", "success", "warning", "error", "dark"],
          },
        }
      : {
          labels: ["Veri Yok"],
          datasets: {
            label: "Tuz Türleri",
            data: [1],
            backgroundColors: ["dark"],
          },
        };
  }, [dashboardData?.saltTypes]);

  // Aylık satışlar için line chart verisi - optimize edilmiş
  const monthlySalesChartData = useMemo(() => {
    return {
      labels: dashboardData?.monthlySales?.map((item) => item.month) || [
        "2025-05",
        "2025-06",
        "2025-07",
      ],
      datasets: [
        {
          label: "Aylık Satış (TL)",
          color: "info",
          data: dashboardData?.monthlySales?.map((item) => parseFloat(item.totalAmount) || 0) || [
            45000, 52000, 60000,
          ],
        },
      ],
    };
  }, [dashboardData?.monthlySales]);

  // Tuz Satışları (Ton) için line chart verisi - optimize edilmiş
  const monthlySaltTonsChartData = useMemo(() => {
    return {
      labels: dashboardData?.monthlySaltTons?.map((item) => item.month) || [
        "2025-05",
        "2025-06",
        "2025-07",
      ],
      datasets: [
        {
          label: "Tuz Satışları (Ton)",
          color: "success",
          data: dashboardData?.monthlySaltTons?.map((item) => parseFloat(item.totalTons) || 0) || [
            65, 70, 77,
          ],
        },
      ],
    };
  }, [dashboardData?.monthlySaltTons]);

  // Sektörel tuz satışları (Ton) için bar chart verisi - optimize edilmiş
  const tonsBySectorChartData = useMemo(() => {
    return {
      labels: dashboardData?.tonsBySector?.map((item) => item.sector) || [
        "Yem Fabrikaları",
        "Diğer Sektörler",
      ],
      datasets: [
        {
          label: "Sektörel tuz satışları (Ton)",
          color: "dark",
          data: dashboardData?.tonsBySector?.map((item) => parseFloat(item.totalTons) || 0) || [
            77, 25,
          ],
        },
      ],
    };
  }, [dashboardData?.tonsBySector]);

  // Nakliye durumları için doughnut chart verisi - optimize edilmiş
  const shipmentStatusesChartData = useMemo(() => {
    return dashboardData?.shipmentStatuses?.length > 0
      ? {
          labels: dashboardData.shipmentStatuses.map((item) => item.status || "Bilinmeyen"),
          datasets: {
            label: "Nakliye Durumları",
            data: dashboardData.shipmentStatuses.map((item) => item.count || 0),
            backgroundColors: ["primary", "success", "warning", "error", "info"],
          },
        }
      : {
          labels: ["Veri Yok"],
          datasets: {
            label: "Nakliye Durumları",
            data: [1],
            backgroundColors: ["dark"],
          },
        };
  }, [dashboardData?.shipmentStatuses]);
  // Kullanıcı yükleniyorsa loading göster
  if (userLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  // Kullanıcı yoksa sign-in sayfasına yönlendir
  if (!userLoading && !user) {
    return <Navigate to="/authentication/sign-in" replace />;
  }

  // ROLE_MANAGER rolüne sahip değilse ana sayfaya yönlendir
  if (!userLoading && user && !user.roles?.includes("ROLE_MANAGER")) {
    return <Navigate to={hasCoreRole ? "/orders/create" : "/demand"} replace />;
  }

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <MDTypography variant="h6" color="error">
            Veri yüklenirken hata oluştu: {error}
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Tarih ve Saat Gösterimi */}
        <MDBox mb={3} display="flex" justifyContent="center">
          <DateTimeDisplay />
        </MDBox>

        {/* Tarih Seçici */}
        <MDBox mb={3}>
          <Card sx={{ p: 2 }}>
            <MDTypography variant="h6" mb={2}>
              Tarih Aralığı Seçin
            </MDTypography>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <TextField
                label="Başlangıç Tarihi"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ minWidth: 200 }}
              />
              <TextField
                label="Bitiş Tarihi"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ minWidth: 200 }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleDateChange}
                disabled={!startDate || !endDate}
                sx={{
                  minWidth: 140,
                  fontWeight: "bold",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    boxShadow: "0 6px 8px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                Verileri Getir
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={handleResetDates}
                sx={{
                  minWidth: 140,
                  fontWeight: "bold",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    boxShadow: "0 6px 8px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                Son 30 Gün
              </Button>
              <Button
                variant="contained"
                color="info"
                onClick={handleLastWeek}
                sx={{
                  minWidth: 140,
                  fontWeight: "bold",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    boxShadow: "0 6px 8px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                Son 7 Gün
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleLastDay}
                sx={{
                  minWidth: 140,
                  fontWeight: "bold",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    boxShadow: "0 6px 8px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                Son 1 Gün
              </Button>
            </Box>
            <MDTypography variant="caption" color="text" mt={1} display="block">
              Tarih seçmezseniz son 30 günlük veriler gösterilir.
              {selectedStartDate && selectedEndDate && (
                <span style={{ color: "green", fontWeight: "bold" }}>
                  {" "}
                  Aktif: {selectedStartDate} - {selectedEndDate}
                </span>
              )}
            </MDTypography>
          </Card>
        </MDBox>

        {/* İstatistik Kartları */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="inventory"
                title="Toplam Müşteri"
                count={dashboardData?.totalCustomerBetweenTime || 0}
                percentage={{
                  color: "success",
                  amount: dashboardData?.totalCustomerCount || 0,
                  label: `Seçili tarih aralığında (Toplam: ${
                    dashboardData?.totalCustomerCount || 0
                  })`,
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="info"
                icon="payments"
                title="Toplam Satış"
                count={`${dashboardData?.totalSalesBetweenTime?.toLocaleString("tr-TR") || 0} ₺`}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "Seçili tarih aralığında",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="success"
                icon="scale"
                title="Toplam Tonaj"
                count={`${dashboardData?.totalTonsBetweenTime || 0} Ton`}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "Seçili tarih aralığında",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MDBox mb={1.5} position="relative">
              <ComplexStatisticsCard
                color="primary"
                icon="local_shipping"
                title="Tahsilat Alınacak Toplam Tutar"
                count={`${
                  dashboardData?.totalPendingPaymentsBetweenTime?.toLocaleString("tr-TR") || 0
                } ₺`}
                percentage={{
                  color: "info",
                  amount: "",
                  label: "Seçili tarih aralığında",
                }}
              />
              <IconButton
                onClick={handlePendingInfoClick}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  color: "info.main",
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                  },
                }}
                size="small"
              >
                <InfoIcon />
              </IconButton>
            </MDBox>
          </Grid>
        </Grid>

        {/* İlk Satır Grafikler */}
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            {/* Tuz Türleri Dağılımı */}
            <Grid item xs={12} lg={6}>
              <MDBox mb={3}>
                <CustomDoughnutChart
                  title="Tuz Türleri Dağılımı"
                  description="Satış yapılan tuz türlerinin dağılımı"
                  chart={saltTypesChartData}
                />
              </MDBox>
            </Grid>

            {/* Aylık Satışlar */}
            <Grid item xs={12} lg={6}>
              <MDBox mb={3}>
                <CustomLineChart
                  color="success"
                  title="Toplam Satış Tutarı"
                  description="Aylık satış performansı"
                  date={getActiveDateRange()}
                  chart={monthlySalesChartData}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        {/* İkinci Satır Grafikler */}
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            {/* Sektörel tuz satışları (Ton) */}
            <Grid item xs={12} lg={4}>
              <MDBox mb={3}>
                <CustomBarChart
                  color="info"
                  title="Sektörel tuz satışları (Ton)"
                  description="En çok satış yapılan sektörler"
                  date={getActiveDateRange()}
                  chart={tonsBySectorChartData}
                />
              </MDBox>
            </Grid>

            {/* Tuz Satışları (Ton) */}
            <Grid item xs={12} lg={4}>
              <MDBox mb={3}>
                <CustomLineChart
                  color="dark"
                  title="Tuz Satışları (Ton)"
                  description="Toplam yapılan tuz satışları"
                  date={getActiveDateRange()}
                  chart={monthlySaltTonsChartData}
                />
              </MDBox>
            </Grid>

            {/* Nakliye Durumları */}
            <Grid item xs={12} lg={4}>
              <MDBox mb={3}>
                <CustomDoughnutChart
                  title="Nakliye Durumları"
                  description="Nakliye durumlarının dağılımı"
                  chart={shipmentStatusesChartData}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        {/* Alt Bölüm - Tablolar */}
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            {/* En Sık Çalışılan Müşteriler */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ height: "100%", p: 2 }}>
                <TopCustomers customers={dashboardData?.topCustomers || []} />
              </Card>
            </Grid>

            {/* En Sık Kullanılan Tuzlar */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ height: "100%", p: 2 }}>
                <SaltSalesBySector sales={dashboardData?.saltSalesBySector || []} />
              </Card>
            </Grid>
          </Grid>
        </MDBox>

        {/* İkinci Satır Tablolar */}
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            {/* En Hızlı Ödeme Yapan Müşteriler */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ height: "100%", p: 2 }}>
                <FastestPayers fastestPayers={dashboardData?.fastestPayers || []} />
              </Card>
            </Grid>

            {/* Kara Liste */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ height: "100%", p: 2 }}>
                <BlackList nonPaidCustomers={dashboardData?.nonPaidCustomers || []} />
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>

      {/* Tahsilat Detayları Dialog */}
      <Dialog open={pendingDialogOpen} onClose={handlePendingDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Tahsilat Alınacak Müşteriler</Typography>
            <IconButton onClick={handlePendingDialogClose} size="small">
              <InfoIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {dashboardData?.customerPendings && dashboardData.customerPendings.length > 0 ? (
            <Box>
              {dashboardData.customerPendings.map((customer, index) => (
                <Card key={index} sx={{ p: 2, mb: 2, backgroundColor: "#f8f9fa" }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" fontWeight="bold">
                      {customer.customerName}
                    </Typography>
                    <Typography variant="h6" color="error" fontWeight="bold">
                      {customer.pendingAmount?.toLocaleString("tr-TR")} ₺
                    </Typography>
                  </Box>
                </Card>
              ))}
              <Box mt={2} p={2} backgroundColor="primary.main" borderRadius={1}>
                <Typography variant="h6" color="white" textAlign="center">
                  Toplam Tahsilat:{" "}
                  {dashboardData.customerPendings
                    .reduce((sum, customer) => sum + (customer.pendingAmount || 0), 0)
                    .toLocaleString("tr-TR")}{" "}
                  ₺
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box textAlign="center" py={3}>
              <Typography variant="body1" color="text.secondary">
                Tahsilat alınacak müşteri bulunmuyor.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default Dashboard;
